import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import Toast, { ToastType } from '../components/Toast.tsx';

interface ToastMessage {
  id: number;
  message: string;
  type: ToastType;
  action?: string;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, action?: string) => void;
  showSuccess: (message: string, action?: string) => void;
  showError: (message: string, action?: string) => void;
  showInfo: (message: string, action?: string) => void;
  showWarning: (message: string, action?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info', action?: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, action }]);
  }, []);

  const showSuccess = useCallback(
    (message: string, action?: string) => showToast(message, 'success', action),
    [showToast]
  );
  const showError = useCallback(
    (message: string, action?: string) => showToast(message, 'error', action),
    [showToast]
  );
  const showInfo = useCallback(
    (message: string, action?: string) => showToast(message, 'info', action),
    [showToast]
  );
  const showWarning = useCallback(
    (message: string, action?: string) => showToast(message, 'warning', action),
    [showToast]
  );

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showInfo, showWarning }}>
      {children}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          action={toast.action}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
